import React, { useEffect } from "react";
import { Box, CircularProgress, Grid, IconButton, Tooltip, Typography } from "@mui/material";
import FiberNewIcon from "@mui/icons-material/FiberNew";
import { BoldTypography } from "./TimelineInfoBarStyle";
import WarningIcon from "@mui/icons-material/Warning";
import {
    useIncompleteTauCount,
    useLastTau,
    useLatestErrorMessage,
    useSelectedTime,
    useShouldAutoRefreshWindow,
    useShouldStopFetchingData
} from "../../../features/surface/surfaceSlice";
import { prettyTimeDesktop, prettyTimeMobile } from "./timelineUtils";
import { MapStyle, useMapStyle } from "../../../features/mapDisplay/mapDisplaySlice";
import { useIsMobile } from "../../../features/pageWidth/pageWidthSlice";
import { MS_IN_DAY } from "../geoUtils";
import { useMaxDataTime, useMinDataTime } from "../../../features/time/TimelineSlice";
import { useHasOngoingFetches, useHasSlowConnection } from "../../../features/time/playbackSlice";
import { SLOW_CONNECTION_WARNING } from "../../UserVisibleStrings";

interface TimelineInfoBarProps
{
    isLoading: boolean;
    dataIsStale: boolean;
    routeStartTime?: number;
    routeEndTime?: number;
    displayTime: number;
    NEW_WILMA_DATA_AVAILABLE: string;
    refetchRiskData: () => void;
}

/**
 * TimelineInfoBar component to display timeline information and loading status.
 *
 * @param {TimelineInfoBarProps} props - The props for the TimelineInfoBar
 * @returns {JSX.Element} The TimelineInfoBar component
 */
const TimelineInfoBar: React.FC<TimelineInfoBarProps> = ( {
    isLoading,
    dataIsStale,
    routeStartTime,
    routeEndTime,
    displayTime,
    NEW_WILMA_DATA_AVAILABLE,
    refetchRiskData
} ) =>
{
    const time = useSelectedTime();
    const displayFormattedTime = routeStartTime !== undefined && routeEndTime !== undefined ? time : displayTime;
    const incompleteTauCount = useIncompleteTauCount();
    const shouldStopFetchingData = useShouldStopFetchingData();
    const hasSlowConnection = useHasSlowConnection();
    const latestErrorMessage = useLatestErrorMessage();
    const mapStyle = useMapStyle();
    const isMobile = useIsMobile();
    const shouldAutoRefreshWindow: boolean = useShouldAutoRefreshWindow();
    const minDisplayTime = useMinDataTime();
    const maxDisplayTime = useMaxDataTime();
    const lastTau = useLastTau();
    const timelineLengthDays = Math.round( ( maxDisplayTime - minDisplayTime ) / MS_IN_DAY );
    const textColor = ( mapStyle === MapStyle.Satellite || mapStyle === MapStyle.Dark ) ? "white" : "black";
    const isFutureTrip = lastTau < minDisplayTime;
    const isFetchingSurfaces = useHasOngoingFetches();

    useEffect( () =>
    {
        if ( dataIsStale && shouldAutoRefreshWindow )
        {
            refetchRiskData();
        }
    }, [ dataIsStale, shouldAutoRefreshWindow ] );

    return (
        <Grid container
            alignItems = "center"
            sx = {{
                position: "relative",
                width: "97%", //setting this to 100% causes issues on wordpress but not localhost
                transform: "TranslateX(20px)",
                display: "flex",
                flexDirection: isMobile ? "column" : "row"
            }}
        >
            <Grid item xs sx = {{ textAlign: "right" }}/>
            {!isMobile &&
                <Grid item xs = "auto">
                    <Grid item md = {12}>
                        <Box
                            sx = {{
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "center",
                                backgroundColor: "lightgrey",
                                padding: "4px 16px",
                                borderRadius: "36px",
                                width: isMobile ? "160px" : "235px",
                            }}
                        >
                            <BoldTypography sx = {{ color: "black", fontSize: isMobile ? "0.8rem" : "1rem" }}>
                                {( routeStartTime !== undefined && routeEndTime !== undefined )
                                    ? ( isFutureTrip || shouldStopFetchingData ) ? "Driving Impacts Unavailable" : "Driving Impacts Along Route"
                                    : ( timelineLengthDays !== 0 ? `${timelineLengthDays}-Day Driving Impacts` : "Loading Driving Impacts" )}
                            </BoldTypography>
                            {isFutureTrip &&
                                <Typography sx = {{ color: "black", fontSize: "0.8rem" }}>
                                    available 8 days before your trip
                                </Typography>}
                            <Typography sx = {{ color: "black", fontSize: "0.8rem" }}>
                                {isMobile ? prettyTimeMobile( displayFormattedTime ) : prettyTimeDesktop( displayFormattedTime )}
                            </Typography>
                        </Box>
                    </Grid>
                </Grid>}
            <Grid item xs>
                {dataIsStale && (
                    <Tooltip title = {NEW_WILMA_DATA_AVAILABLE}>
                        <IconButton onClick = {refetchRiskData} sx = {{ color: textColor }}>
                            <FiberNewIcon/>
                        </IconButton>
                    </Tooltip>
                )}
                {shouldStopFetchingData && (
                    <Tooltip
                        title = {latestErrorMessage !== undefined ? `${latestErrorMessage}` : `Failed to fetch ${incompleteTauCount} forecasts.`}
                    >
                        <WarningIcon style = {{ color: "orange", marginLeft: "8px" }}/>
                    </Tooltip>
                )}
                {hasSlowConnection && (
                    <Tooltip
                        title = {SLOW_CONNECTION_WARNING}
                    >
                        <WarningIcon style = {{ color: "orange", marginLeft: "8px" }}/>
                    </Tooltip>
                )}
                { ( isLoading || isFetchingSurfaces ) && !shouldStopFetchingData && /* Show the spinny wheel when either actively querying for surfaces and the data is not glacial. */
                    <Tooltip title = "Loading forecast data">
                        <CircularProgress size = {15}/>
                    </Tooltip>}
            </Grid>
            {isMobile && (
                <Grid item xs = "auto">
                    <Grid item md = {12}>
                        <BoldTypography sx = {{ color: textColor }}>
                            {isMobile ? prettyTimeMobile( displayFormattedTime ) : prettyTimeDesktop( displayFormattedTime )}
                        </BoldTypography>
                    </Grid>
                </Grid>
            )}
        </Grid>
    );
};

export default TimelineInfoBar;
